import { en, type Messages } from "./messages/en";

/**
 * Adding a language is a catalogue-only change:
 *   1. copy `messages/en/` to `messages/<code>/` and translate the values (keep the keys and the {placeholders});
 *      type its index `satisfies Messages`, so a missing key does not compile;
 *   2. register it below.
 * No locale segment in the URLs: the language is negotiated from the Accept-Language header (see server.ts).
 * Dates and numbers do not wait for a catalogue: they follow the browser's locale (format.ts).
 */
export const DEFAULT_LOCALE = "en";

export const CATALOGUES: Record<string, Messages> = { en };

/** Used for dates and numbers when the request names no language. Day-month-year, 24 h: unambiguous. */
export const DEFAULT_FORMAT_LOCALE = "en-GB";

/** The first language tag of an Accept-Language header that Intl accepts ("fr-CH,fr;q=0.9" -> "fr-CH"). */
export function firstLanguageTag(acceptLanguage: string | null | undefined): string | undefined {
    for (const part of (acceptLanguage ?? "").split(",")) {
        const tag = part.split(";")[0]?.trim();
        if (!tag || tag === "*") continue;
        try {
            return Intl.getCanonicalLocales(tag)[0];
        } catch {
            // not a language tag: try the next one
        }
    }
    return undefined;
}

/** The catalogue to use: the first Accept-Language entry whose primary language we have, else English. */
export function negotiateLocale(
    acceptLanguage: string | null | undefined,
    available: readonly string[] = Object.keys(CATALOGUES),
): string {
    for (const part of (acceptLanguage ?? "").split(",")) {
        const tag = part.split(";")[0]?.trim().toLowerCase();
        if (!tag) continue;
        const exact = available.find((code) => code.toLowerCase() === tag);
        const primary = available.find((code) => code.toLowerCase() === tag.split("-")[0]);
        if (exact ?? primary) return (exact ?? primary) as string;
    }
    return DEFAULT_LOCALE;
}
